import EmptyList from 'components/common/EmptyList';
import TicketPauseRegistrationModal from 'components/modals/TicketPauseRegistrationModal';
import useApi from 'helpers/api';
import { formatTime } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import is from 'is_js';
import { useKanbanContext } from 'providers/KanbanProvider';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import Skeleton from 'react-loading-skeleton';
import TicketsService from 'services/Tickets.service';
import KanbanColumn from './KanbanColumn';
import KanbanModal from './KanbanModal';

const KanbanContainer = ({ filters }) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const {
    kanbanState: { kanbanModal },
    kanbanDispatch
  } = useKanbanContext();

  const [kanbanItems, setKanbanItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [timeSpent, setTimeSpent] = useState('');

  // Regras de movimentação
  const movementRules = {
    'A fazer': ['Atendendo', 'Concluído'], // A Fazer pode ir para Atendendo ou Concluído
    Atendendo: ['Pausado'], // Atendendo só pode ir para Pausado
    Pausado: ['Atendendo', 'Concluído'], // Pausado pode ir para Atendendo ou Concluído
    Concluído: ['Pausado'] // Concluído pode ir para Pausado
  };

  const getKanbanItems = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await TicketsService.listKanban(queryParams);
      if (response) {
        setKanbanItems(response);
      }
    } catch (error) {
      showErrorNotification('Erro ao carregar tickets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (is.ipad()) {
      containerRef.current.classList.add('ipad');
    }

    if (is.mobile()) {
      containerRef.current.classList.add('mobile');
      if (is.safari()) {
        containerRef.current.classList.add('safari');
      }
      if (is.chrome()) {
        containerRef.current.classList.add('chrome');
      }
    }
  }, []);

  useEffect(() => {
    getKanbanItems();
  }, [filters]);

  const getColumn = id => {
    return kanbanItems.find(item => item.id === Number(id));
  };

  const reorderArray = (array, fromIndex, toIndex) => {
    const newArr = [...array];

    const chosenItem = newArr.splice(fromIndex, 1)[0];
    newArr.splice(toIndex, 0, chosenItem);

    return newArr;
  };

  const move = (source, destination) => {
    const sourceItemsClone = [...getColumn(source.droppableId).items];
    const destItemsClone = [...getColumn(destination.droppableId).items];

    const [removedItem] = sourceItemsClone.splice(source.index, 1);
    destItemsClone.splice(destination.index, 0, removedItem);

    return {
      updatedDestItems: destItemsClone,
      updatedSourceItems: sourceItemsClone
    };
  };

  const canMoveTicket = (fromStatus, toStatus) => {
    const allowedMoves = movementRules[fromStatus] || [];
    const canMove = allowedMoves.includes(toStatus);
    return canMove;
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    setIsUpdating(true);
    try {
      let response;

      switch (newStatus) {
        case 'Atendendo':
          response = await TicketsService.start(ticketId);
          break;
        case 'Concluído':
          response = await TicketsService.complete(ticketId);
          break;
        case 'Pausado':
          response = await TicketsService.reopen(ticketId);
          break;
        default:
          showErrorNotification('Status não suportado');
          return;
      }

      if (response && response.ok) {
        showSuccessNotification('Ticket atualizado com sucesso');
        getKanbanItems();
      } else {
        showErrorNotification('Erro ao atualizar ticket');
      }
    } catch (error) {
      showErrorNotification('Erro ao atualizar ticket');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePauseTicket = async ticketId => {
    const response = await TicketsService.getTimeSpent(ticketId);
    if (response && !response.userMessage) {
      setTimeSpent(formatTime(response));
      setShowPauseModal(true);
    }
  };

  const handlePauseSubmit = async pauseData => {
    if (!selectedTicket) return;

    setIsUpdating(true);
    try {
      const response = await TicketsService.pause(selectedTicket.id, pauseData);

      if (response && response.status) {
        showErrorNotification(response.userMessage || 'Erro ao pausar ticket');
      } else {
        showSuccessNotification('Ticket pausado com sucesso');
        setShowPauseModal(false);
        setSelectedTicket(null);
        getKanbanItems();
      }
    } catch (error) {
      showErrorNotification('Erro ao pausar ticket');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClosePauseModal = () => {
    setShowPauseModal(false);
    setSelectedTicket(null);
  };

  const handleDragEnd = async result => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const ticketId = parseInt(draggableId.replace('ticket-', ''));
    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;

    // Verificar regras de movimentação
    if (!canMoveTicket(fromStatus, toStatus)) {
      showErrorNotification('Movimentação não permitida');
      return;
    }

    // Se está movendo para PAUSED, abrir modal
    if (fromStatus === 'Atendendo' && toStatus === 'Pausado') {
      const ticket = kanbanItems
        .flatMap(column => column.tickets || [])
        .find(t => t.id === ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
        handlePauseTicket(ticket.id);
        return;
      }
    }

    // Atualizar status do ticket
    await updateTicketStatus(ticketId, toStatus);
  };

  const isEmpty = kanbanItems.every(
    item => !item.tickets || item.tickets.length === 0
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className="kanban-container me-n3 scrollbar bg-100"
        ref={containerRef}
      >
        {' '}
        {isLoading ? (
          <Skeleton count={10} height={44} baseColor="#5E6E82" />
        ) : isEmpty ? (
          <EmptyList imgSize={500} vhSize={83} />
        ) : (
          kanbanItems.map(kanbanColumnItem => (
            <KanbanColumn
              key={kanbanColumnItem.id}
              kanbanColumnItem={kanbanColumnItem}
            />
          ))
        )}
        <KanbanModal show={kanbanModal.show} />
      </div>
      <TicketPauseRegistrationModal
        ticketId={selectedTicket?.id}
        data={timeSpent}
        showModal={showPauseModal}
        handleClose={handleClosePauseModal}
        handleSubmit={handlePauseSubmit}
        isLoading={isUpdating}
      />
    </DragDropContext>
  );
};

export default KanbanContainer;
